import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  inject,
} from "@angular/core";
import CustomSwipe from "@vision/core-map-api/src/controls/CustomSwipe";
import { COLLECTION_AOI_Z_INDEX_TOP } from "@vision/core-map-api/src/constants/CoreMapConstants";
import { ProjectService } from "../../services/internal-services/project.service";
import { IProjectAOI } from "../../interfaces/i-project-aoi";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../shared/model/google-analytics-constants";
import { MapService } from "../../services/internal-services/map.service";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { ProjectStatus } from "../../model/project-status";
import { FeatureFlagsService } from "../../services/internal-services/feature-flags.service";
import { FeatureFlags } from "../../model/feature-flags";
import { GoogleAnalyticsService } from "../../../shared/services/google-analytics.service";
import { VIEWER_SERVICE } from "../../services/internal-services/viewer-services/inject-base-viewer.service";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-swipe-viewer",
  templateUrl: "./swipe-viewer.component.html",
  styleUrls: ["./swipe-viewer.component.scss"],
  standalone: false,
})
export class SwipeViewerComponent implements OnDestroy {
  viewerService = inject(VIEWER_SERVICE);

  FeatureFlags = FeatureFlags;

  active = false;

  private _rightAOI: IProjectAOI = null;

  private _leftAOI: IProjectAOI = null;

  private _discreteEnabled: boolean = false;

  private _disable: boolean = true;

  private _dualView: boolean = false;

  private _geotiffStatus: boolean = false;

  enableByZoom: boolean = false;

  hasProjects: boolean = false;

  disableBySearch: boolean = false;

  swipeControl: CustomSwipe;

  @Output() visibleEvent = new EventEmitter<boolean>();

  @Output() sbsRightLayer = new EventEmitter<IProjectAOI>();

  @Output() sbsLeftLayer = new EventEmitter<IProjectAOI>();

  @Input()
  set dualView(dualView: boolean) {
    this._dualView = dualView;
    this.disable = dualView || this.geotiffStatus || !this.hasProjects || !this.enableByZoom || this.disableBySearch;
  }

  @Input()
  set discreteEnabled(discrete: boolean) {
    this._discreteEnabled = discrete;
    if (discrete && this.active) {
      this.disableSwipe();
    }
  }

  @Input()
  set geotiffStatus(geotiffStatus: boolean) {
    this._geotiffStatus = geotiffStatus;
    this.disable =
      this.dualView || this.geotiffStatus || !this.hasProjects || !this.enableByZoom || this.disableBySearch;
  }

  @Input()
  set projectStatus(status: ProjectStatus) {
    this.hasProjects = !!status?.enabledAOIs?.length;
    this.disable =
      this.dualView || this.geotiffStatus || !this.hasProjects || !this.enableByZoom || this.disableBySearch;
  }

  @Input()
  set zoomEvent(evt: any) {
    if (evt) {
      this.enableByZoom = evt.zoom >= ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL;
      this.disable =
        this.dualView || this.geotiffStatus || !this.hasProjects || !this.enableByZoom || this.disableBySearch;
    }
  }

  //TODO: THIS HAS TO BE WELL IMPLEMENTED BY NOT CLOSING THE TOOL WHEN SEARCHING
  @Input()
  set searchEvent(evt: any) {
    if (evt) {
      this.disableBySearch = evt.search;
      this.disable =
        this.dualView || this.geotiffStatus || !this.hasProjects || !this.enableByZoom || this.disableBySearch;
    }
  }

  @Input()
  set status(status: any) {
    if (status?.enable) {
      this.activeSwipe();
      this.leftAOI = status.left;
      this.rightAOI = status.right;
    }
  }

  set disable(disable: boolean) {
    if (disable !== this._disable) {
      this._disable = disable;
      if (disable) {
        this.disableSwipe();
      }
    }
  }

  constructor(
    protected googleAnalyticsService: GoogleAnalyticsService,
    protected mapService: MapService,
    protected flagService: FeatureFlagsService,
    protected cdr: ChangeDetectorRef,
  ) {}

  ngOnDestroy() {
    if (this.swipeControl) {
      this.disableSwipe();
    }
  }

  @Input()
  set leftAOI(aoi: IProjectAOI) {
    if (aoi && this.leftAOI?.getLayerId() !== aoi.getLayerId()) {
      if (this._leftAOI?.getLayerId() !== this._rightAOI?.getLayerId()) this.removeForceDownload(this._leftAOI);
      this._leftAOI = aoi;
      this.showLeftAOI();
      this.updateLayer();
      this.sbsLeftLayer.emit(aoi);
      this.mapService.getOverlayManager().triggerMapStatus();
    }
  }

  @Input()
  set rightAOI(aoi: IProjectAOI) {
    if (aoi && this.rightAOI?.getLayerId() !== aoi.getLayerId()) {
      if (this._rightAOI?.getLayerId() !== this._leftAOI?.getLayerId()) this.removeForceDownload(this._rightAOI);
      this._rightAOI = aoi;
      this.showRightAOI();
      this.updateLayer(true);
      this.sbsRightLayer.emit(aoi);
      this.mapService.getOverlayManager().triggerMapStatus();
    }
  }

  toggleSwipe() {
    if (this.active) {
      this.disableSwipe();
    } else {
      this.activeSwipe();
    }
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.BEFORE_AFTER_TOOL,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      this.active,
    );
  }

  activeSwipe() {
    this.viewerService.resetTimelineFilters();

    this.active = true;
    this.visibleEvent.emit(this.active);
    const map = this.mapService.getCoreMap();

    this.swipeControl = new CustomSwipe({
      map,
    });

    const visibleProjects = ProjectService.visibleProjects(this.viewerService.getProjectStatus().enabledAOIs);

    this.rightAOI = visibleProjects.length > 1 ? visibleProjects[1] : visibleProjects[0];
    this.leftAOI = visibleProjects[0];

    this.viewerService.enableContextMenu();
  }

  showLeftAOI() {
    this.setDefaultAOIVisibility();
    const mapLayer = this.getLeftMapLayer();

    // Set plus 3 because we need to reserve +3 for avoid strange behaviour when switch between layers
    mapLayer.setZIndex(COLLECTION_AOI_Z_INDEX_TOP + 3);
    mapLayer.set("forceDownload", true);
    if (this.leftAOI && this.rightAOI && this.leftAOI.getLayerId() !== this.rightAOI.getLayerId()) {
      mapLayer.getSource().refresh();
    }
  }

  showRightAOI() {
    this.setDefaultAOIVisibility();
    const mapLayer = this.getRightMapLayer();
    // Set plus 3 because we need to reserve +2 for avoid strange behaviour when switch between layers
    mapLayer.setZIndex(COLLECTION_AOI_Z_INDEX_TOP + 2);
    mapLayer.set("forceDownload", true);
    if (this.leftAOI && this.rightAOI && this.leftAOI.getLayerId() !== this.rightAOI.getLayerId()) {
      mapLayer.getSource().refresh();
    }
  }

  getLeftMapLayer() {
    return this.mapService.getOverlayManager().findAOI(this.leftAOI.getName());
  }

  getRightMapLayer() {
    return this.mapService.getOverlayManager().findAOI(this.rightAOI.getName());
  }

  setDefaultAOIVisibility() {
    if (!this.leftAOI || !this.rightAOI) {
      return;
    }

    //Get all enables AOIS except right and left AOIs and set default order
    const visibleProjects = ProjectService.visibleProjects(this.viewerService.getProjectStatus().enabledAOIs);
    visibleProjects
      .filter((p) => p.getLayerId() !== this.leftAOI?.getLayerId() && p.getLayerId() !== this.rightAOI?.getLayerId())
      .map((project) => {
        const mapAOI = this.mapService.getOverlayManager().findAOI(project.getName());
        mapAOI.resetOrder();
      });
  }

  updateLayer(right?: boolean) {
    if (!this.swipeControl) {
      return;
    }

    const mapAOIs = ProjectService.visibleProjects(this.viewerService.getProjectStatus().enabledAOIs).map((project) =>
      this.mapService.getOverlayManager().findAOI(project.getName()),
    );

    if (this.leftAOI && this.rightAOI) {
      //Clone the initial array
      let leftLayer = mapAOIs.find((aoi) => aoi.getId() === this.leftAOI?.getLayerId());
      let rightLayer = mapAOIs.find((aoi) => aoi.getId() === this.rightAOI?.getLayerId());

      if (this.leftAOI.getLayerId() === this.rightAOI.getLayerId()) {
        this.removeAllLayers();
      } else if (this.swipeControl.layers?.length < 2) {
        this.removeAllLayers();
        this.swipeControl.addLayer(leftLayer);
        this.swipeControl.addLayer(rightLayer, true);
      } else {
        this.removeSideLayer(right);
        if (right) this.swipeControl.addLayer(rightLayer, true);
        else this.swipeControl.addLayer(leftLayer);
      }
    }

    const status = {
      enable: this.active,
      getAOIAtPoint: this.getAOIAtPoint.bind(this),
      left: this.leftAOI,
      right: this.rightAOI,
    };

    this.updateStatus(status);
  }

  removeAllLayers() {
    this.swipeControl.removeLayers();
  }

  removeSideLayer(right: boolean) {
    this.swipeControl?.layers
      .filter((item) => item.right === right)
      .map((item) => this.swipeControl.removeLayer(item.layer));
  }

  /**
   * When removing this parameter for the AOI we´re forcing that this layer it´s no longer on any sides of the before/after
   * @param aoi
   */
  removeForceDownload(aoi) {
    if (aoi) {
      const mapLayer = this.mapService.getOverlayManager().findAOI(aoi.getName());
      mapLayer.set("forceDownload", false);
    }
  }

  disableSwipe() {
    this.active = false;
    this.visibleEvent.emit(this.active);

    const projectStatus = this.viewerService.getProjectStatus();
    if (projectStatus && this.leftAOI) {
      this.viewerService.updateProjectStatus(
        projectStatus.aois,
        this.leftAOI.getName(),
        this.mapService.getMapStatus(),
      );
    }

    if (this.swipeControl) {
      this._leftAOI = null;
      this._rightAOI = null;
      this.swipeControl.dispatch(null);
      this.swipeControl = null;
    }
    const status = {
      enable: this.active,
      getAOIAtPoint: null,
    };
    this.disableBySearch = false;
    this.updateStatus(status);

    this.viewerService.enableContextMenu();
    this.viewerService.updateCenterImageDate();
    this.viewerService.updateUrlParameters(true);
    this.cdr.detectChanges();
  }

  updateStatus(status: any) {
    this.mapService.swipeStatus = status;
    this.mapService.getMapStatus().swipeStatus = status;
  }

  getAOIAtPoint(point) {
    const sideForPoint = this.swipeControl?.getSide(point);

    return sideForPoint === "left" ? this.leftAOI : this.rightAOI;
  }

  get leftAOI(): IProjectAOI {
    return this._leftAOI;
  }

  get rightAOI(): IProjectAOI {
    return this._rightAOI;
  }

  get disable(): boolean {
    return this._disable;
  }

  get dualView(): boolean {
    return this._dualView;
  }

  get discreteEnabled(): boolean {
    return this._discreteEnabled;
  }

  get geotiffStatus(): boolean {
    return this._geotiffStatus;
  }

  getTooltip() {
    if (this.dualView) {
      return "Dual-view is not compatible with the slider";
    }
    if (!this.viewerService.getProjectStatus()?.enabledAOIs?.length) {
      return "Before/After is not available in this area";
    }
    if (!this.viewerService.show2DTimeline()) {
      return "Zoom in to enable Before/After";
    }
    if (this.geotiffStatus) {
      return "Before/After is not available when Export GeoTIFF tool is open";
    }

    return "Before/After";
  }
}
